import { Badge, BadgeColor, BadgePlacement, BadgePosition, BadgeRounded, Button, ButtonColor, Container, ContainerSize, Gap, Heading, Stack, Text } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type BadgePageProps = {};
const BadgePage = ({ }: BadgePageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Badge
            </Heading>
            <Text el="p"><Text el="strong">Badge</Text> are small, compact elements used to display labels, counts, or
                statuses. In React bootstrap, badges are typically used for indicating status or providing contextual
                information about an item, such as showing unread message counts, notifications, or labels associated
                with categories.</Text>
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Headings
            </Heading>
            <Text className="mb-4">
                The <Text el="strong">Badge</Text> component is wrapped inside an <Text el="strong">heading</Text> tag.
                Because HTML elements inherit font-size and styling from their parent, the badge automatically adjusts
                to match the size of the heading.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Heading el="h1"><Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge></Heading>
                    <Heading el="h2"><Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge></Heading>
                    <Heading el="h3"><Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge></Heading>
                    <Heading el="h4"><Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge></Heading>
                    <Heading el="h5"><Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge></Heading>
                    <Heading el="h6"><Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge></Heading>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BadgeWithHeadingCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Buttons
            </Heading>
            <Text className="mb-4">
                <Text el="strong">Badges</Text>  can be used inside <Text el="strong">links or buttons</Text> to highlight counts, notifications, or statuses.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Button className="btn btn-primary">Message<Badge color={BadgeColor.Secondary} className="ms-2">9</Badge></Button>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BadgeWithButtonCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Badges — Positioning
            </Heading>
            <Text className="mb-4">
                <Text el="strong">Badges</Text> can be positioned over other components (such as buttons) using absolute
                positioning utilities. Use the <Text el="strong">position</Text> and <Text
                    el="strong">placement</Text> property to control where the badge appears.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap3}>
                        <Button color={ButtonColor.Primary} className="position-relative">Counter
                            <Badge position={BadgePosition.Absolute} placement={BadgePlacement.TopEnd}
                                color={BadgeColor.Danger}>3</Badge>
                        </Button>
                        <Button color={ButtonColor.Primary} className="position-relative">Counter
                            <Badge position={BadgePosition.Absolute} placement={BadgePlacement.BottomStart}
                                color={BadgeColor.Danger}>3</Badge>
                        </Button>
                        <Button color={ButtonColor.Primary} className="position-relative">Counter
                            <Badge position={BadgePosition.Absolute} placement={BadgePlacement.TopStart}
                                color={BadgeColor.Danger}>3</Badge>
                        </Button>
                        <Button color={ButtonColor.Primary} className="position-relative">Counter
                            <Badge position={BadgePosition.Absolute} placement={BadgePlacement.BottomEnd}
                                color={BadgeColor.Danger}>3</Badge>
                        </Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BadgeWithPositionAndPlacementPropCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-2">
                <Text el="strong">Available Placements:</Text> TopStart, TopCenter, TopEnd, MiddleStart, MiddleCenter,
                MiddleEnd, BottomStart, BottomCenter, BottomEnd.
            </Text>

            <Text className="mb-4">
                <Text el="strong">Available positions:</Text> Absolute, Relative, Static.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Background colors
            </Heading>
            <Text className="mb-4">
                <Text el="strong">Badges</Text> support different <Text el="strong">color</Text> variants. Use the color prop to change the appearance of a badge.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Badge color={BadgeColor.Primary}>Primary</Badge>
                        <Badge color={BadgeColor.Secondary}>Secondary</Badge>
                        <Badge color={BadgeColor.Success}>Success</Badge>
                        <Badge color={BadgeColor.Danger}>Danger</Badge>
                        <Badge color={BadgeColor.Warning}>Warning</Badge>
                        <Badge color={BadgeColor.Info}>Info</Badge>
                        <Badge color={BadgeColor.Light}>Light</Badge>
                        <Badge color={BadgeColor.Dark}>Dark</Badge>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BadgeWithBackgroundColorCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Border-radius
            </Heading>
            <Text className="mb-4">
                <Text el="strong">Badges</Text> can have different border-radius styles. Use the  <Text el="strong">rounded</Text> prop to change the shape of the badge.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Badge color={BadgeColor.Primary} rounded={BadgeRounded.Circle} className="p-2">Primary</Badge>
                        <Badge color={BadgeColor.Secondary} rounded={BadgeRounded.Pill} className="p-2">Secondary</Badge>
                        <Badge color={BadgeColor.Success} rounded={BadgeRounded.None} className="p-2">Success</Badge>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BadgeWithBorderRadiusCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
}
export default BadgePage;

const BadgeWithHeadingCode = `<Heading el="h1">
    <Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge>
</Heading>
<Heading el="h2">
    <Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge>
</Heading>
<Heading el="h3">
    <Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge>
</Heading>
<Heading el="h4">
    <Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge>
</Heading>
<Heading el="h5">
    <Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge>
</Heading>
<Heading el="h6">
    <Badge color={BadgeColor.Secondary}>Badge with parent heading</Badge>
</Heading>`;

const BadgeWithButtonCode = `<Button className="btn btn-primary">
    Message
    <Badge color={BadgeColor.Secondary} className="ms-2">9</Badge>
</Button>`;

const BadgeWithPositionAndPlacementPropCode = `<Stack gap={Gap.Gap3}>
    <Button color={ButtonColor.Primary} className="position-relative">Counter
        <Badge position={BadgePosition.Absolute} placement={BadgePlacement.TopEnd} color={BadgeColor.Danger}>3</Badge>
    </Button>
    <Button color={ButtonColor.Primary} className="position-relative">Counter
        <Badge position={BadgePosition.Absolute} placement={BadgePlacement.BottomStart} color={BadgeColor.Danger}>3</Badge>
    </Button>
    <Button color={ButtonColor.Primary} className="position-relative">Counter
        <Badge position={BadgePosition.Absolute} placement={BadgePlacement.TopStart} color={BadgeColor.Danger}>3</Badge>
    </Button>
    <Button color={ButtonColor.Primary} className="position-relative">Counter
        <Badge position={BadgePosition.Absolute} placement={BadgePlacement.BottomEnd} color={BadgeColor.Danger}>3</Badge>
    </Button>
</Stack>`;

const BadgeWithBackgroundColorCode = `<Stack gap={Gap.Gap2}>
    <Badge color={BadgeColor.Primary}>Primary</Badge>
    <Badge color={BadgeColor.Secondary}>Secondary</Badge>
    <Badge color={BadgeColor.Success}>Success</Badge>
    <Badge color={BadgeColor.Danger}>Danger</Badge>
    <Badge color={BadgeColor.Warning}>Warning</Badge>
    <Badge color={BadgeColor.Info}>Info</Badge>
    <Badge color={BadgeColor.Light}>Light</Badge>
    <Badge color={BadgeColor.Dark}>Dark</Badge>
</Stack>`;

const BadgeWithBorderRadiusCode = `<Stack gap={Gap.Gap2}>
    <Badge color={BadgeColor.Primary} rounded={BadgeRounded.Circle} className="p-2">Primary</Badge>
    <Badge color={BadgeColor.Secondary} rounded={BadgeRounded.Pill} className="p-2">Secondary</Badge>
    <Badge color={BadgeColor.Success} rounded={BadgeRounded.None} className="p-2">Success</Badge>
</Stack>`;










